import { Component, Input, OnChanges, SimpleChanges, OnDestroy, EventEmitter, Output } from '@angular/core';
import { GroupsService } from '../services/groups.service';
import { BehaviorSubject, Subject, forkJoin } from 'rxjs';
import { takeUntil, filter, flatMap, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Sort } from '@angular/material/sort';
import { SelectMultipleUsersDialogComponent } from './selectMultipleUsersDialog.component';

@Component({
    selector: 'group-members',
    styles: [`
        .usersPanel {
            overflow-y: auto;
            height: calc(100vh - 290px);
        }
        .cdk-column-firstName.mat-column-firstName {
            width: 232px;
        }
        .cdk-column-lastName.mat-column-lastName {
            width: 232px;
        }
    `],
    template: `
        <mat-toolbar *ngIf="!displayOnly">
            <span fxFlex></span>
            <button mat-button color="primary" (click)="addMembers()">Add Members</button>
        </mat-toolbar>
        <div class="usersPanel"
             infiniteScroll 
             [infiniteScrollThrottle]="300"
             [infiniteScrollDistance]="1"
             [scrollWindow]="false"
             (scrolled)="nextPage()">
            <table mat-table
                matSort 
                (matSortChange)="sort($event)"
                [matSortDisableClear]="true "
                [matSortActive]="params?.value?.sort" 
                [matSortDirection]="params?.value?.sortDirection"  
                [dataSource]="users" 
                style="width: 100%;">
                <ng-container matColumnDef="firstName">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="firstNameLower" width="20%">First Name</th>
                    <td mat-cell *matCellDef="let row">{{row.firstName}}</td>
                </ng-container>
                <ng-container matColumnDef="lastName">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="lastNameLower" width="20%">Last Name</th>
                    <td mat-cell *matCellDef="let row">{{row.lastName}}</td>
                </ng-container>
                <ng-container matColumnDef="emailAddress">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="emailAddress">Email Address</th>
                    <td mat-cell *matCellDef="let row">{{row.emailAddress}}</td>
                </ng-container>
                <ng-container matColumnDef="status">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="status">Status</th>
                    <td mat-cell *matCellDef="let row">{{ row.status }}</td>
                </ng-container>
                <ng-container matColumnDef="actions">
                    <th mat-header-cell *matHeaderCellDef></th>
                    <td mat-cell *matCellDef="let row">
                        <button mat-icon-button (click)="removeMember(row)" style="float: right;">
                            <i class="material-icons">clear</i>
                        </button>
                    </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="columns"></tr>
                <tr mat-row *matRowDef="let row; columns: columns;"></tr>
            </table>
            <div *ngIf="noTouchingPaging" fxLayoutAlign="center center">
                <spinner></spinner>
            </div>
            <div *ngIf="users | async as results">
                <div *ngIf="!results.length">
                    <h5 style="text-align: center">
                        <p>No users have been selected yet for this Group.</p>
                        <p>Choose "Add Members" to select desired members.</p>
                    </h5>
                </div>
            </div>
        </div>
    `
})
export class GroupMembersComponent implements OnChanges, OnDestroy {
    private cleanupTime = new Subject<void>();
    private baseColumns = ['firstName', 'lastName', 'emailAddress', 'status'];
    columns = this.baseColumns.concat(['actions']);
    @Input() group;
    @Input() displayOnly: boolean;
    @Output() membersChanged = new EventEmitter<void>(); 
    params = new BehaviorSubject<any>({ 
        sort: 'firstNameLower',
        sortDirection: 'asc',
        page: 0, 
        rows: 40
    });
    users = new BehaviorSubject<any[]>([]);
    lastFetched: boolean;
    noTouchingPaging: boolean;

    constructor(private snackBar: MatSnackBar,
                private dialog: MatDialog, 
                private groupsService: GroupsService) {
        this.params
        .pipe(
            filter(params => params.groupId),
            flatMap(params => this.groupsService.getUsers(params)),
            tap(response => this.lastFetched = response.last),
            tap(() => this.noTouchingPaging = false),
            takeUntil(this.cleanupTime)
        ).subscribe(response => this.users.next(this.users.value.concat(response.content)));
    }

    nextPage() {
        if (!this.lastFetched && !this.noTouchingPaging) {
            const params = this.params.value;
            params.page = params.page + 1;
            this.noTouchingPaging = true;
            this.params.next(params);
        }
    }

    sort(sort: Sort) {
        const params = this.params.value;
        params.page = 0;
        params.sort = sort.active;
        params.sortDirection = sort.direction;
        this.noTouchingPaging = true;
        this.users.next([]);
        this.params.next(params);        
    }

    private refetch() {
        this.noTouchingPaging = true;
        this.users.next([]);
        this.params.next(Object.assign({}, this.params.value, { page: 0 }));
    } 

    removeMember(user) {
        this.groupsService.removeUser(this.group.id, user.id)
        .subscribe(
            () => {
                this.membersChanged.emit();
                this.snackBar.open(`${user.firstName} ${user.lastName} has been removed from ${this.group.name}.`, 'OK', { duration: 3000});
                this.refetch();
            },
            () => this.snackBar.open(`Unable to remove ${user.firstName} ${user.lastName} please try again.`, 'OK', { duration: 3000})
        )
    }

    addMembers() {
        this.dialog.open(SelectMultipleUsersDialogComponent, { data: { existingUserIds: this.group.members } }).afterClosed()
        .pipe(
            filter(users => !!users),
            flatMap(users => forkJoin(users.map(user => this.groupsService.addUser(this.group.id, user.id)))),
            tap(() => this.membersChanged.emit()),
            tap(() => this.refetch()),
        ).subscribe(results => this.snackBar.open(`${results.length} user${results.length > 1 ? 's' : ''} added to ${this.group.name}.`, 'OK', { duration: 3000}));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.displayOnly) {
            this.columns = changes.displayOnly.currentValue ? this.baseColumns : this.baseColumns.concat(['actions']);   
        }
        if (changes.group && changes.group.currentValue && !this.params.value.groupId) {
            this.params.next(Object.assign({}, this.params.value, { groupId: this.group.id }));
        }
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
        this.params.complete();
        this.users.complete();
    }
}